// EDEN · coach-voice playback — the iOS-safe path (2026-07-01 rebuild).
//
// WHY THIS SHAPE: the old playback hand-scheduled raw PCM chunks on a Web Audio clock at a
// hardcoded 24 kHz. On iOS that silently failed — Safari only reliably plays audio that flows from
// a user gesture, and the coach's voice arrives several seconds AFTER the tap (post STT+CHAT+TTS).
// Now the coach reply is a complete WAV clip per turn, played through ONE <audio> element that we
// "unlock" during the first tap (iOS then allows later programmatic play()). Simple and robust.

// 20 ms of silence — played once inside a user gesture to satisfy iOS autoplay, so subsequent
// programmatic play() calls (which happen after an await) are allowed.
const SILENT_WAV =
  "data:audio/wav;base64,UklGRmQBAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YUABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA";

export class Playback {
  private el: HTMLAudioElement;
  private unlocked = false;

  constructor() {
    this.el = new Audio();
    this.el.preload = "auto";
    // iOS: keep audio inline (never take over the screen with the native player).
    this.el.setAttribute("playsinline", "");
  }

  /** Call INSIDE a user gesture (the first tap) so iOS will permit later programmatic play(). */
  unlock(): void {
    if (this.unlocked) return;
    this.unlocked = true;
    this.el.src = SILENT_WAV;
    void this.el.play().catch(() => undefined);
  }

  /** Play one coach clip (base64 WAV). Replaces whatever was playing. */
  play(wavB64: string | null | undefined): void {
    if (!wavB64) return;
    this.el.src = `data:audio/wav;base64,${wavB64}`;
    try {
      this.el.currentTime = 0;
    } catch {
      /* setting currentTime before load can throw on some browsers — safe to ignore */
    }
    void this.el.play().catch(() => undefined);
  }

  /** Barge-in / leaving: stop the coach immediately. */
  flush(): void {
    try {
      this.el.pause();
    } catch {
      /* already paused */
    }
  }

  /** No-op: an <audio> element resumes itself after a tab-backgrounding (kept for API parity). */
  resume(): void {
    /* intentionally empty */
  }

  close(): void {
    this.flush();
    this.el.removeAttribute("src");
  }
}
